import {useState, useMemo, useCallback} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {PersonalEmailSequenceToView} from 'shells/row/personal-email-sequence-row/types';

import type {
    DripProgressionFragment,
    EmailAutomationStatusesFragment,
    WidgetFragment,
} from 'nutshell-graphql-types';

import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../core/use-get-session-user';

import {DripSequenceDetailsModal} from './drip-sequence-details-modal';
import {EmailSequencesTab} from './email-sequences-tab';
import {useCancelDripProgression} from './graphql/hooks/use-cancel-drip-progression';
import {useCancelManuallyStartedEmailSequence} from './graphql/hooks/use-cancel-manually-started-email-sequence';
import {useCancelPersonalEmailSequence} from './graphql/hooks/use-cancel-personal-email-sequence';
import {useSendQueuedEmailNow} from './graphql/hooks/use-send-queued-email-now';
import {useStartPersonalEmailSequence} from './graphql/hooks/use-start-personal-email-sequence';
import {
    flattenDripProgressions,
    getEmptyStateProps,
    getTabProps,
    getCurrentPersonalEmailSequenceToView,
    separateEmailAutomationStatuses,
    getManuallyStartedEmailSequences,
} from './helpers';
import {PersonalSequenceDetailsModal} from './personal-sequence-details-modal';
import type {
    PersonalEmailSequenceToViewSparse,
    ContactWithDripProgressions,
    QueuedEmailWithSequence,
    PostMutationCallbacks,
} from './types';

interface Props {
    widget: WidgetFragment;
    contactsWithDripProgressions: ContactWithDripProgressions[];
    emailAutomationStatuses: EmailAutomationStatusesFragment[];
    queuedEmails: QueuedEmailWithSequence[] | null | undefined;
    isFirstLoadingDripSequences: boolean;
    isFirstLoadingEmailAutomationStatuses: boolean;
    isFirstLoadingManuallyStartedSequences: boolean;
    postMutationCallbacks?: PostMutationCallbacks;
}

export function EmailSequencesWidget(props: Props) {
    const {
        contactsWithDripProgressions,
        emailAutomationStatuses,
        queuedEmails,
        postMutationCallbacks,
    } = props;

    const dripSequences = useMemo(
        () => flattenDripProgressions(contactsWithDripProgressions),
        [contactsWithDripProgressions]
    );

    const [dripSequenceToView, setDripSequenceToView] = useState<
        | {
              dripSequence: DripProgressionFragment | null | undefined;
              contactId: string;
              contactName: string;
          }
        | null
        | undefined
    >(null);
    const [personalEmailSequenceToView, setPersonalEmailSequenceToView] = useState<
        PersonalEmailSequenceToViewSparse | null | undefined
    >(null);
    const [selectedTab, setSelectedTab] = useState<string>('active');
    const [error, setError] = useState<string | null | undefined>(null);

    const {user} = useGetSessionUser();

    const {cancelDripProgression} = useCancelDripProgression();
    const {cancelManuallyStartedEmailSequence} = useCancelManuallyStartedEmailSequence();
    const {cancelPersonalEmailSequence} = useCancelPersonalEmailSequence();
    const {startPersonalEmailSequence} = useStartPersonalEmailSequence();
    const {sendQueuedEmailNow} = useSendQueuedEmailNow();

    const separatedEmailAutomationStatuses = useMemo(
        () => separateEmailAutomationStatuses(emailAutomationStatuses),
        [emailAutomationStatuses]
    );

    const manuallyStartedEmailSequences = useMemo(
        () => getManuallyStartedEmailSequences(queuedEmails, emailAutomationStatuses),
        [queuedEmails, emailAutomationStatuses]
    );

    const currentPersonalEmailSequence: PersonalEmailSequenceToView | null | undefined =
        useMemo(() => {
            if (!personalEmailSequenceToView) {
                return null;
            }

            return getCurrentPersonalEmailSequenceToView(
                manuallyStartedEmailSequences,
                separatedEmailAutomationStatuses,
                personalEmailSequenceToView
            );
        }, [
            manuallyStartedEmailSequences,
            personalEmailSequenceToView,
            separatedEmailAutomationStatuses,
        ]);

    const handleCancelPersonalEmailSequence = useCallback(
        (emailAutomationStatusId: string) => {
            cancelPersonalEmailSequence({emailAutomationStatus: {id: emailAutomationStatusId}})
                .then(() => postMutationCallbacks?.cancelPersonal?.())
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [cancelPersonalEmailSequence, postMutationCallbacks]
    );

    const handleCancelManuallyStartedEmailSequence = useCallback(
        (emailSequenceId: string) => {
            cancelManuallyStartedEmailSequence({emailSequence: {id: emailSequenceId}})
                .then(() => postMutationCallbacks?.cancelManual?.())
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [cancelManuallyStartedEmailSequence, postMutationCallbacks]
    );

    const handleCancelDripSequence = useCallback(
        (id: string) => {
            cancelDripProgression({dripProgressionId: id})
                .then(() => postMutationCallbacks?.cancelDrip?.())
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [cancelDripProgression, postMutationCallbacks]
    );

    const handleStartPersonalEmailSequence = useCallback(
        (id: string) => {
            startPersonalEmailSequence({id})
                .then(() => postMutationCallbacks?.startPersonal?.())
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [startPersonalEmailSequence, postMutationCallbacks]
    );

    const handleSendEmailNow = useCallback(
        (emailId: string) => {
            sendQueuedEmailNow({queuedEmail: {id: emailId}})
                .then(() => postMutationCallbacks?.sendNow?.())
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [sendQueuedEmailNow, postMutationCallbacks]
    );

    const handleSeeFullDripSequence = useCallback(
        (id: string, contactId: string, contactName: string) => {
            setDripSequenceToView({
                dripSequence: dripSequences.find((dripSequence) => dripSequence.id === id),
                contactId: contactId,
                contactName: contactName,
            });
        },
        [dripSequences]
    );

    const emptyStateProps = useMemo(() => {
        return getEmptyStateProps(
            dripSequences ? dripSequences.length : 0,
            separatedEmailAutomationStatuses.activeStatuses.length,
            separatedEmailAutomationStatuses.finishedStatuses.length,
            manuallyStartedEmailSequences
                ? manuallyStartedEmailSequences.activeSequences.length
                : 0,
            manuallyStartedEmailSequences
                ? manuallyStartedEmailSequences.finishedSequences.length
                : 0,
            selectedTab
        );
    }, [
        separatedEmailAutomationStatuses,
        dripSequences,
        manuallyStartedEmailSequences,
        selectedTab,
    ]);

    const activeCount = useMemo(() => {
        const activeDripSequencesCount = dripSequences ? dripSequences.length : 0;
        const activeEmailAutomationStatusesCount =
            separatedEmailAutomationStatuses.activeStatuses.length;
        const activeManuallyStartedEmailSequencesCount = manuallyStartedEmailSequences
            ? manuallyStartedEmailSequences.activeSequences.length
            : 0;

        return (
            activeDripSequencesCount +
            activeEmailAutomationStatusesCount +
            activeManuallyStartedEmailSequencesCount
        );
    }, [dripSequences, separatedEmailAutomationStatuses, manuallyStartedEmailSequences]);

    const completeCount = useMemo(() => {
        const finishedEmailAutomationStatusesCount =
            separatedEmailAutomationStatuses.finishedStatuses.length;
        const finishedManuallyStartedEmailSequencesCount = manuallyStartedEmailSequences
            ? manuallyStartedEmailSequences.finishedSequences.length
            : 0;

        return finishedEmailAutomationStatusesCount + finishedManuallyStartedEmailSequencesCount;
    }, [separatedEmailAutomationStatuses, manuallyStartedEmailSequences]);

    const tabProps = useMemo(() => {
        return getTabProps(activeCount, completeCount, selectedTab, setSelectedTab);
    }, [activeCount, completeCount, selectedTab]);

    const isLoading = useMemo(() => {
        if (selectedTab === 'active') {
            return (
                props.isFirstLoadingEmailAutomationStatuses ||
                props.isFirstLoadingManuallyStartedSequences ||
                props.isFirstLoadingDripSequences
            );
        } else if (selectedTab === 'completed') {
            return (
                props.isFirstLoadingEmailAutomationStatuses ||
                props.isFirstLoadingManuallyStartedSequences
            );
        }

        return true;
    }, [
        props.isFirstLoadingDripSequences,
        props.isFirstLoadingEmailAutomationStatuses,
        props.isFirstLoadingManuallyStartedSequences,
        selectedTab,
    ]);

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={isLoading}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
                tabProps={{tabs: tabProps}}
                count={activeCount}
            >
                <>
                    {selectedTab === 'active' && (
                        // @ts-expect-error
                        <EmailSequencesTab
                            tab='active'
                            dripSequences={dripSequences}
                            manuallyStartedEmailSequences={
                                manuallyStartedEmailSequences.activeSequences
                            }
                            personalEmailSequences={separatedEmailAutomationStatuses.activeStatuses}
                            onSeeDripSequenceDetails={handleSeeFullDripSequence}
                            onSeePersonalEmailSequenceDetails={setPersonalEmailSequenceToView}
                            onCancelAutomatedEmailSequence={handleCancelPersonalEmailSequence}
                            onStartAutomatedEmailSequence={handleStartPersonalEmailSequence}
                            onCancelManuallyStartedEmailSequence={
                                handleCancelManuallyStartedEmailSequence
                            }
                            onCancelDripSequence={handleCancelDripSequence}
                        />
                    )}
                    {selectedTab === 'completed' && (
                        // @ts-expect-error
                        <EmailSequencesTab
                            tab='completed'
                            manuallyStartedEmailSequences={
                                manuallyStartedEmailSequences.finishedSequences
                            }
                            personalEmailSequences={
                                separatedEmailAutomationStatuses.finishedStatuses
                            }
                            onSeeDripSequenceDetails={handleSeeFullDripSequence}
                            onSeePersonalEmailSequenceDetails={setPersonalEmailSequenceToView}
                        />
                    )}
                </>
            </CollapsibleCardWidget>
            {dripSequenceToView && (
                <DripSequenceDetailsModal
                    dripSequence={dripSequenceToView.dripSequence}
                    contactId={dripSequenceToView.contactId}
                    contactName={dripSequenceToView.contactName}
                    onCancelSequence={(id: string) => handleCancelDripSequence(id)}
                    onClose={() => setDripSequenceToView(null)}
                />
            )}
            {currentPersonalEmailSequence && (
                <PersonalSequenceDetailsModal
                    personalEmailSequence={currentPersonalEmailSequence}
                    onCancelSequence={(id: string) =>
                        currentPersonalEmailSequence.type === 'manually-started'
                            ? handleCancelManuallyStartedEmailSequence(id)
                            : handleCancelPersonalEmailSequence(id)
                    }
                    onClose={() => setPersonalEmailSequenceToView(null)}
                    onStartSequence={(id: string) => handleStartPersonalEmailSequence(id)}
                    onSendEmailNow={(emailId: string) => handleSendEmailNow(emailId)}
                    userId={user && user.id}
                />
            )}
        </>
    );
}
